import React, { useState, useEffect, useCallback } from 'react';
import ApiClient from '../utils/ApiClient';

// Manage the ASINs a single SFP product (GTIN) is listed under: view linked ASINs,
// auto-discover by GTIN, manually add, and unlink. Linking auto-creates the ASIN's
// SFP New + Used-Like New listings.
export default function ManageAsinsModal({ open, product, onClose }) {
    const gtin = product?.gtin;
    const [asins, setAsins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [candidates, setCandidates] = useState(null);
    const [discovering, setDiscovering] = useState(false);
    const [selected, setSelected] = useState({});
    const [manualAsin, setManualAsin] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const loadAsins = useCallback(async () => {
        if (!gtin) return;
        setLoading(true);
        try {
            const res = await ApiClient.post('/api/sfp-inventory/asins', { gtin });
            setAsins(res?.data?.asins || []);
        } catch (e) {
            setMessage('Failed to load ASINs');
        } finally {
            setLoading(false);
        }
    }, [gtin]);

    useEffect(() => {
        if (open) {
            setCandidates(null);
            setSelected({});
            setManualAsin('');
            setMessage(null);
            loadAsins();
        }
    }, [open, loadAsins]);

    const handleDiscover = async () => {
        setDiscovering(true);
        setMessage(null);
        try {
            const res = await ApiClient.post('/api/sfp-inventory/discover-asins', { gtin });
            setCandidates(res?.data?.candidates || []);
        } catch (e) {
            setMessage('Discovery failed');
        } finally {
            setDiscovering(false);
        }
    };

    const linkAsin = async (asin, source) => {
        setBusy(true);
        setMessage(null);
        try {
            const res = await ApiClient.post('/api/sfp-inventory/link-asin', { gtin, asin, source });
            const lr = res?.data?.listingResults || [];
            const created = lr.filter(r => r.status === 'success').length;
            const failed = lr.filter(r => r.status === 'error').length;
            setMessage(`Linked ${asin} — ${created} listing(s) created${failed ? `, ${failed} failed` : ''}`);
            await loadAsins();
            setCandidates(c => (c ? c.filter(x => x.asin !== asin) : c));
            setManualAsin('');
        } catch (e) {
            setMessage(`Failed to link ${asin}`);
        } finally {
            setBusy(false);
        }
    };

    const linkSelected = async () => {
        const toLink = Object.keys(selected).filter(a => selected[a]);
        for (const a of toLink) {
            // eslint-disable-next-line no-await-in-loop
            await linkAsin(a, 'discovered');
        }
        setSelected({});
    };

    const unlink = async (asin) => {
        if (!window.confirm(`Unlink ${asin}? Quantity syncing stops for it (the Amazon listing stays live).`)) return;
        setBusy(true);
        setMessage(null);
        try {
            await ApiClient.post('/api/sfp-inventory/unlink-asin', { gtin, asin });
            await loadAsins();
        } catch (e) {
            setMessage(`Failed to unlink ${asin}`);
        } finally {
            setBusy(false);
        }
    };

    const handleManualAdd = () => {
        const a = manualAsin.trim().toUpperCase();
        if (!/^[A-Z0-9]{10}$/.test(a)) {
            setMessage('Enter a valid 10-character ASIN');
            return;
        }
        linkAsin(a, 'manual');
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} aria-hidden="true" />
            <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Manage ASINs</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {product?.product_title || 'Product'} · UPC {gtin}
                    </p>
                </div>

                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-6">
                    {message && (
                        <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">{message}</div>
                    )}

                    {/* Linked ASINs */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Linked ASINs</h4>
                        {loading ? (
                            <p className="text-sm text-gray-500">Loading…</p>
                        ) : (
                            <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                                {asins.map((a) => (
                                    <li key={a.asin} className="flex items-center justify-between px-3 py-2">
                                        <span className="flex items-center gap-2">
                                            <span className="font-mono text-sm text-gray-900">{a.asin}</span>
                                            {a.primary ? (
                                                <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">primary</span>
                                            ) : (
                                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">secondary</span>
                                            )}
                                        </span>
                                        {!a.primary && (
                                            <button
                                                type="button"
                                                onClick={() => unlink(a.asin)}
                                                disabled={busy}
                                                className="text-xs font-medium text-red-600 hover:text-red-500 disabled:opacity-50"
                                            >
                                                Unlink
                                            </button>
                                        )}
                                    </li>
                                ))}
                                {asins.length === 0 && (
                                    <li className="px-3 py-2 text-sm text-gray-500">No ASINs linked.</li>
                                )}
                            </ul>
                        )}
                    </div>

                    {/* Manual add */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Add an ASIN manually</h4>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={manualAsin}
                                onChange={(e) => setManualAsin(e.target.value)}
                                placeholder="B0XXXXXXXX"
                                className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                            />
                            <button
                                type="button"
                                onClick={handleManualAdd}
                                disabled={busy}
                                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                                Link + create listings
                            </button>
                        </div>
                    </div>

                    {/* Auto-discover */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-900">Auto-discover by UPC</h4>
                            <button
                                type="button"
                                onClick={handleDiscover}
                                disabled={discovering}
                                className="text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                            >
                                {discovering ? 'Searching…' : 'Discover'}
                            </button>
                        </div>
                        {candidates && (
                            candidates.length === 0 ? (
                                <p className="text-sm text-gray-500">No new ASINs found for this UPC.</p>
                            ) : (
                                <div className="space-y-2">
                                    <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                                        {candidates.map((c) => (
                                            <li key={c.asin} className="flex items-center gap-3 px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={!!selected[c.asin]}
                                                    onChange={(e) => setSelected((s) => ({ ...s, [c.asin]: e.target.checked }))}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <span className="font-mono text-sm text-gray-900">{c.asin}</span>
                                                <span className="text-xs text-gray-500 truncate">{c.title || ''}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        type="button"
                                        onClick={linkSelected}
                                        disabled={busy || !Object.values(selected).some(Boolean)}
                                        className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                                    >
                                        Link selected + create listings
                                    </button>
                                </div>
                            )
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
